'use client'

/**
 * The layout-level fallback.
 *
 * `error.tsx` is mounted inside the root layout, so it cannot catch a throw in
 * `layout.tsx` or in `providers.tsx` — the two files every screen in the console renders
 * inside. This boundary replaces the whole document, which is why it carries its own
 * `<html>` and `<body>`: at the point it renders, the layout that would have supplied
 * them is the thing that failed.
 *
 * It is deliberately styleless. It cannot rely on `globals.css` having loaded, and a
 * fallback that depends on the subsystem that failed is not a fallback.
 */

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  return (
    <html lang="en">
      <body style={{ fontFamily: 'system-ui, sans-serif', margin: 0, padding: '3rem 2rem' }}>
        <main role="alert" style={{ maxWidth: '44rem' }}>
          <p style={{ fontSize: '0.75rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            The Solicitation Command Center could not start
          </p>
          <h1 style={{ fontSize: '1.5rem', margin: '0.5rem 0 1rem' }}>
            The application shell itself failed to render.
          </h1>
          <p style={{ lineHeight: 1.6, margin: '0 0 1rem' }}>
            This is a failure in the layout or in the providers that wrap every screen, so
            no screen is reachable and nothing on this page reflects the state of any
            acquisition. No record has been changed.
          </p>
          <p style={{ lineHeight: 1.6, margin: '0 0 1rem' }}>
            {error.message || 'The failure carried no message.'}
          </p>
          <p style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.75rem' }}>
            {error.digest ? `digest ${error.digest}` : 'no digest was attached to this failure'}
          </p>
          <button type="button" onClick={reset} style={{ marginTop: '1rem', padding: '0.5rem 1rem' }}>
            Try rendering the shell again
          </button>
        </main>
      </body>
    </html>
  )
}
